import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Trash2, ChevronRight, Search, Plus, ChevronDown } from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import EmptyState from "../../components/shared/EmptyState";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import Input from "../../components/Inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import { FormLabel } from "../../components/Labels/labels";
import Loader from "../../components/Loader/Loader";
import { AuthError } from "../../api";
import { listAdminGroups, createAdminGroup, deleteAdminGroup } from "../../api/adminGroups";
import { registerGoogleWorkspaceGroup, getGoogleWorkspaceConfig } from "../../api/adminGoogleWorkspace";
import GroupDetailPanel from "../../components/Settings/GroupDetailPanel";
import useFeedback from "../../hooks/useFeedback";
import UserAuthStore from "../../store/userStore";
import UIStore from "../../store/uiStore";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes";
import CustomTooltip from "../../components/Tooltip/CustomTooltip";

// ─── Source badge ─────────────────────────────────────────────────────────────
const SOURCE_STYLES = {
  photon_native: {
    badge: "bg-fill-blue-50 text-ink-blue-600 dark:bg-fill-blue-600/20 dark:text-ink-blue-300",
    label: "Photon",
  },
  scim: {
    badge: "bg-fill-green-50 text-ink-green-600 dark:bg-fill-green-600/20 dark:text-ink-green-300",
    label: "IdP / SCIM",
  },
  google_workspace: {
    badge: "bg-fill-hex-fff7ed text-ink-orange-600 dark:bg-fill-orange-600/20 dark:text-ink-orange-300",
    label: "Google Workspace",
  },
};

const SourceBadge = ({ source }) => {
  const s = SOURCE_STYLES[source] ?? SOURCE_STYLES.photon_native;
  return (
    <span className={`photon-metadata-label inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${s.badge}`}>
      {s.label}
    </span>
  );
};

// ─── Policy mapped badge ──────────────────────────────────────────────────────
const PolicyBadge = ({ mapped }) =>
  mapped ? (
    <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-fill-green-100 text-ink-green-800 dark:bg-fill-green-600/20 dark:text-ink-green-300">
      Mapped
    </span>
  ) : (
    <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-fill-gray-100 text-ink-gray-500 dark:bg-fill-gray-700 dark:text-ink-gray-400">
      None
    </span>
  );

// ─── Inline create form (same pattern as InviteForm in UsersPage) ───────────────────────────────
const InlineCreateForm = ({ onCancel, onCreate }) => {
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitted(true);
    const trimmed = displayName.trim();
    if (!trimmed) return;
    setError("");
    setLoading(true);
    try {
      await onCreate({ display_name: trimmed, description: description.trim() });
      onCancel();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to create group.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      key="inline-create-form"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl mb-4 p-5">
        <p className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide mb-4">
          New Group
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 flex flex-col gap-1.5">
            <FormLabel htmlFor="gp-name">
              Name <span className="text-ink-red-600">*</span>
            </FormLabel>
            <Input
              id="gp-name"
              placeholder="e.g. Engineering"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
              className={submitted && !displayName.trim() ? "border-edge-red-600 dark:border-edge-red-600" : ""}
            />
          </div>
          <div className="flex-1 flex flex-col gap-1.5">
            <FormLabel htmlFor="gp-desc">
              Description <span className="text-ink-gray-400 font-normal text-xs">(optional)</span>
            </FormLabel>
            <Input
              id="gp-desc"
              placeholder="Short description of this group"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>
        </div>

        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-xs text-ink-red-600 dark:text-ink-red-300 mb-3"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <div className="flex justify-end gap-3">
          <SecondaryButton onClick={onCancel} disabled={loading}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={loading || !displayName.trim()}>
            {loading ? "Creating…" : "Create Group"}
          </PrimaryButton>
        </div>
      </div>
    </motion.div>
  );
};
// ─── Inline add Google Workspace group form ────────────────────────────────────
const InlineAddGwGroupForm = ({ onCancel, onAdd }) => {
  const [groupEmail, setGroupEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitted(true);
    const trimmed = groupEmail.trim().toLowerCase();
    if (!trimmed) return;
    setError("");
    setLoading(true);
    try {
      await onAdd(trimmed);
      onCancel();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "google_workspace_config_missing") {
        setError("Google Workspace is not configured for this org. Set up the domain in Settings → Google Workspace.");
      } else if (code === "google_workspace_group_not_found") {
        setError("Group not found in Google Workspace. Check the email address and try again.");
      } else {
        setError(err?.response?.data?.error || err.message || "Failed to register group.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      key="inline-gw-form"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl mb-4 p-5">
        <p className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide mb-4">
          Add Google Workspace Group
        </p>

        <div className="flex flex-col gap-1.5 mb-4">
          <FormLabel htmlFor="gw-group-email">
            Group Email <span className="text-ink-red-600">*</span>
          </FormLabel>
          <Input
            id="gw-group-email"
            placeholder="e.g. engineering@acme.com"
            value={groupEmail}
            onChange={(e) => setGroupEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoFocus
            className={submitted && !groupEmail.trim() ? "border-edge-red-600 dark:border-edge-red-600" : ""}
          />
          <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500">
            The group must exist in your Google Workspace directory. Members will be resolved automatically at login.
          </p>
        </div>

        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-xs text-ink-red-600 dark:text-ink-red-300 mb-3"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <div className="flex justify-end gap-3">
          <SecondaryButton onClick={onCancel} disabled={loading}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={loading || !groupEmail.trim()}>
            {loading ? "Registering…" : "Add Group"}
          </PrimaryButton>
        </div>
      </div>
    </motion.div>
  );
};
// ─── Group row ────────────────────────────────────────────────────────────────
const GroupRow = ({ group, onDelete, onSelect }) => {
  const [pendingDelete, setPendingDelete] = useState(false);
  const isScimManaged = group.source === "scim";

  const createdAt = group.created_at
    ? new Date(group.created_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

  return (
    <>
      <motion.tr
        layout
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        className="group border-b border-edge-gray-100 dark:border-edge-gray-700 hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-surface/60 cursor-pointer transition-colors"
        onClick={() => onSelect(group)}
      >
        {/* Name */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="photon-avatar w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-ink dark:text-ink-gray-200 truncate">
                {group.display_name || group.name || group.group_id}
              </p>
              {group.external_id && group.external_id !== (group.display_name || group.name) && (
                <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500 truncate">{group.external_id}</p>
              )}
            </div>
          </div>
        </td>

        {/* Source */}
        <td className="px-4 py-3">
          <SourceBadge source={group.source} />
        </td>

        {/* Members */}
        <td className="px-4 py-3 text-sm text-ink-gray-700 dark:text-ink-gray-300">
          {group.member_count ?? "—"}
        </td>

        {/* Policy mapped */}
        <td className="px-4 py-3">
          <PolicyBadge mapped={!!group.policy_mapped} />
        </td>

        {/* Created */}
        <td className="px-4 py-3 text-sm text-ink-gray-400 dark:text-ink-gray-500 whitespace-nowrap">
          {createdAt}
        </td>

        {/* Actions */}
        <td
          className="px-4 py-3 text-right"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {isScimManaged ? (
              <span
                className="text-xs text-ink-gray-400 dark:text-ink-gray-500 px-2"
                title="Managed by your IdP — delete the group in your identity provider"
              >
                IdP managed
              </span>
            ) : group.policy_mapped ? (
              <CustomTooltip content="Remove the group policy before deleting" side="top" delayDuration={0}>
                <button
                  disabled
                  className="p-1.5 rounded-lg text-ink-gray-300 dark:text-ink-gray-600 cursor-not-allowed"
                  tabIndex={-1}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </CustomTooltip>
            ) : (
              <button
                onClick={() => setPendingDelete(true)}
                className="p-1.5 rounded-lg hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300 transition-colors"
                title="Delete group"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <ChevronRight className="w-4 h-4 text-ink-gray-400" />
          </div>
        </td>
      </motion.tr>

      <ConfirmDialog
        open={pendingDelete}
        title="Delete Group"
        confirmLabel="Delete"
        confirmVariant="danger"
        onCancel={() => setPendingDelete(false)}
        onConfirm={() => {
          setPendingDelete(false);
          onDelete(group.group_id);
        }}
      >
        Delete <strong>{group.display_name}</strong>? This removes all memberships. The group policy
        document will be retained as an archive.
      </ConfirmDialog>
    </>
  );
};

// ─── Add group dropdown ───────────────────────────────────────────────────────
const AddGroupDropdown = ({ onNewGroup, onAddGwGroup, gwConnected }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleGwClick = () => {
    setOpen(false);
    if (gwConnected) {
      onAddGwGroup();
    } else {
      navigate(ROUTES.SETTINGS.GW_CONFIG);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-fill-gradient-primary text-ink-on-primary hover:bg-fill-primary-hover-alt transition-colors"
      >
        <Plus className="w-4 h-4" />
        Group
        <ChevronDown className="w-3.5 h-3.5 opacity-80" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 mt-1.5 w-60 rounded-xl shadow-lg border border-edge-gray-200 dark:border-edge-gray-700 bg-surface-card-deep dark:bg-surface-card-deep z-20 overflow-hidden"
          >
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-ink-ink dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-surface transition-colors"
              onClick={() => { setOpen(false); onNewGroup(); }}
            >
              Photon Group
            </button>
            <button
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-ink-ink dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-surface transition-colors"
              onClick={handleGwClick}
              title={gwConnected ? undefined : "Google Workspace not connected — click to configure"}
            >
              <span>Google Workspace Group</span>
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  gwConnected
                    ? "bg-fill-green-600"
                    : "bg-fill-amber-400"
                }`}
              />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const GroupsPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const orgId = UserAuthStore((state) => state.orgId);
  const orgAvailable = UIStore((state) => state.orgAvailable);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showAddGwGroup, setShowAddGwGroup] = useState(false);
  const [gwConnected, setGwConnected] = useState(false);
  // selectedGroup will be used by GroupDetailPanel (Step 3)
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Guard: redirect non-admins
  useEffect(() => {
    if (orgAvailable === false) {
      navigate(ROUTES.SETTINGS.MODELS, { replace: true });
    }
  }, [orgAvailable, navigate]);

  useEffect(() => {
    getGoogleWorkspaceConfig()
      .then((cfg) => setGwConnected(!!cfg))
      .catch(() => setGwConnected(false));
  }, []);

  const fetchGroups = useCallback(async () => {
    // Use getState() to always read the latest value, avoiding stale-closure
    // races on initial mount (mirrors the pattern in UsersPage.fetchUsers)
    const currentOrgId = UserAuthStore.getState().orgId;
    if (!currentOrgId) return;
    setLoading(true);
    try {
      const data = await listAdminGroups(currentOrgId);
      setGroups(data ?? []);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load groups.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleCreate = async ({ display_name, description }) => {
    try {
      await createAdminGroup({ display_name, description });
      showFeedback(`Group "${display_name}" created.`, "success");
      await fetchGroups();
    } catch {
      showFeedback("Failed to create group.", "error");
      throw new Error("create failed");
    }
  };

  const handleDelete = async (groupId) => {
    try {
      await deleteAdminGroup(groupId);
      setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
      showFeedback("Group deleted.", "success");
    } catch (err) {
      showFeedback("Failed to delete group.", "error");
    }
  };

  const handleAddGwGroup = async (groupEmail) => {
    try {
      await registerGoogleWorkspaceGroup(groupEmail);
      showFeedback(`Google Workspace group "${groupEmail}" registered.`, "success");
      await fetchGroups();
    } catch (err) {
      // Re-throw so the inline form can display the specific error message
      throw err;
    }
  };

  const filtered = groups.filter((g) =>
    (g.display_name || g.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <SettingsPageShell
      title="Groups"
      description="Organize your team members into groups to manage shared access policies, model permissions, and role-based controls across your organisation."
      maxWidth="5xl"
    >
      {/* Toolbar — only when groups exist */}
      {!loading && groups.length > 0 && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups…"
              className="photon-field w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-dialog-surface text-ink-ink dark:text-ink-gray-200 placeholder:text-ink-gray-400 focus:outline-none focus:ring-2 focus:ring-focus-primary/30"
            />
          </div>
          <AddGroupDropdown
            onNewGroup={() => { setShowCreate((v) => !v); setShowAddGwGroup(false); }}
            onAddGwGroup={() => { setShowAddGwGroup((v) => !v); setShowCreate(false); }}
            gwConnected={gwConnected}
          />
        </div>
      )}

      {/* Inline create form */}
      <AnimatePresence>
        {showCreate && (
          <InlineCreateForm
            onCancel={() => setShowCreate(false)}
            onCreate={handleCreate}
          />
        )}
      </AnimatePresence>

      {/* Inline add GW group form */}
      <AnimatePresence>
        {showAddGwGroup && (
          <InlineAddGwGroupForm
            onCancel={() => setShowAddGwGroup(false)}
            onAdd={handleAddGwGroup}
          />
        )}
      </AnimatePresence>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : groups.length === 0 && !showCreate && !showAddGwGroup ? (
        <EmptyState
          icon={<Users className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
          title="No groups yet"
          description="Create a Photon group or link a Google Workspace group to organise your team and apply shared access policies."
          actionNode={
            <AddGroupDropdown
              onNewGroup={() => { setShowCreate(true); setShowAddGwGroup(false); }}
              onAddGwGroup={() => { setShowAddGwGroup(true); setShowCreate(false); }}
              gwConnected={gwConnected}
            />
          }
        />
      ) : (
        <div className="rounded-2xl border border-edge-gray-200 dark:border-edge-gray-700 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-muted-card dark:bg-surface-muted-card border-b border-edge-gray-200 dark:border-edge-gray-700">
                <th className="px-4 py-3 text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
                  Name
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
                  Source
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
                  Members
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">
                  Policy
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide hidden sm:table-cell">
                  Created
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-ink-gray-400 dark:text-ink-gray-500">
                      No groups match your search.
                    </td>
                  </tr>
                ) : (
                  filtered.map((group) => (
                    <GroupRow
                      key={group.group_id}
                      group={group}
                      onDelete={handleDelete}
                      onSelect={setSelectedGroup}
                    />
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* GroupDetailPanel */}
      <AnimatePresence>
        {selectedGroup && (
          <GroupDetailPanel
            group={selectedGroup}
            orgId={orgId}
            onClose={() => setSelectedGroup(null)}
            onGroupUpdated={fetchGroups}
          />
        )}
      </AnimatePresence>
    </SettingsPageShell>
  );
};

export default GroupsPage;
